/**
 * TabToggle Component
 *
 * Supports two usage modes:
 * 1. Binary mode (two tabs): Uses leftLabel/rightLabel with 'left'|'right' values
 * 2. Multi-tab mode: Uses options array with dynamic number of tabs
 *
 * Examples:
 *
 * Binary mode:
 * <TabToggle
 *   leftLabel="Informations"
 *   rightLabel="Documents"
 *   activeTab="left"
 *   onToggle={(tab) => setTab(tab)}
 * />
 *
 * Multi-tab mode:
 * <TabToggle
 *   options={[
 *     { value: 'documents', label: 'Documents', icon: FileText },
 *     { value: 'informations', label: 'Informations', icon: Info },
 *   ]}
 *   value="documents"
 *   onChange={(value) => setTab(value)}
 * />
 */

import type { LucideIcon } from 'lucide-react';

// Binary mode props
interface BinaryTabToggleProps {
  leftLabel: string;
  rightLabel: string;
  activeTab: 'left' | 'right';
  onToggle: (tab: 'left' | 'right') => void;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
  options?: never;
  value?: never;
  onChange?: never;
}

// Multi-tab mode props
interface MultiTabToggleProps {
  options: Array<{
    value: string;
    label: string;
    icon?: LucideIcon;
  }>;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  leftLabel?: never;
  rightLabel?: never;
  activeTab?: never;
  onToggle?: never;
  leftIcon?: never;
  rightIcon?: never;
}

type TabToggleProps = BinaryTabToggleProps | MultiTabToggleProps;

export function TabToggle(props: TabToggleProps) {
  const disabled = props.disabled || false;

  // Multi-tab mode
  if ('options' in props && props.options) {
    const { options, value, onChange } = props;

    return (
      <div className={`inline-flex items-center bg-neutral-100 rounded-xl p-1 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        {options.map((option) => {
          const Icon = option.icon;
          const isActive = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => !disabled && onChange(option.value)}
              disabled={disabled}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                isActive
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              } ${disabled ? 'cursor-not-allowed' : ''}`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  // Binary mode (backward compatible)
  const { leftLabel, rightLabel, activeTab, onToggle, leftIcon, rightIcon } = props;

  return (
    <div className={`inline-flex items-center bg-neutral-100 rounded-xl p-1 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <button
        type="button"
        onClick={() => !disabled && onToggle('left')}
        disabled={disabled}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
          activeTab === 'left'
            ? 'bg-white text-neutral-900 shadow-sm'
            : 'text-neutral-600 hover:text-neutral-900'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        {leftIcon}
        {leftLabel}
      </button>
      <button
        type="button"
        onClick={() => !disabled && onToggle('right')}
        disabled={disabled}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
          activeTab === 'right'
            ? 'bg-white text-neutral-900 shadow-sm'
            : 'text-neutral-600 hover:text-neutral-900'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        {rightIcon}
        {rightLabel}
      </button>
    </div>
  );
}
